import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Compass, Loader2 } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { setSeoMetadata } from '../../lib/seo';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import { useActivityMarketplaceGate, API } from './activityMarketplaceShared';
import { categoryUrl } from '../../lib/activityUrls';
import ActivitySearch from '../../components/ActivitySearch';
import axios from 'axios';

const ActivitiesDirectoryPage = () => {
  const ready = useActivityMarketplaceGate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState([]);
  const [query, setQuery] = useState('');
  const handleFilter = useCallback((next, q) => { setVisible(next); setQuery(q); }, []);

  useEffect(() => {
    setSeoMetadata({ title: 'All Activities', description: 'Browse every tour, charter and excursion on St John by activity type.',
      canonicalPath: window.location.pathname,
    });
  }, []);

  useEffect(() => {
    if (!ready) return;
    // main categories only - sub-categories live on their parent's landing page
    axios.get(`${API}/api/public/tours-charters/categories`, { params: { top_level_only: true } })
      .then((r) => { const list = r.data || []; setCategories(list); setVisible(list); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ready]);

  if (!ready) return <div className="min-h-screen bg-white"><Header /></div>;

  return (
    <div className="min-h-screen bg-white" data-testid="activities-directory-page">
      <Header />
      <section className="relative overflow-hidden pt-36 pb-12">
        <div className="absolute inset-0 bg-gradient-to-b from-teal-900/30 via-transparent to-transparent" />
        <div className="relative mx-auto max-w-6xl px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-teal-300/80">Explore</p>
            <h1 className="mt-2 text-4xl font-black text-slate-900 sm:text-5xl lg:text-6xl">All Activities</h1>
            <p className="mt-4 max-w-2xl text-base text-slate-600">Browse tours and charters by activity type. <Link to="/activities/companies" className="text-teal-300 underline hover:text-teal-700">Looking for a specific charter company?</Link></p>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6">
        <ActivitySearch
          items={categories}
          onFilter={handleFilter}
          placeholder="Search activities, type of activity, length of tour or tags..."
          testId="all-activities-search"
        />
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-24 pt-10">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-teal-400" /></div>
        ) : categories.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white py-20 text-center text-slate-600" data-testid="activities-directory-empty">
            No activity categories yet. Check back soon!
          </div>
        ) : (
          <>
          {query && (
            <p className="mb-4 text-sm font-semibold text-slate-700" data-testid="categories-filtered-count">
              {visible.length} matching categor{visible.length === 1 ? 'y' : 'ies'}
            </p>
          )}
          {visible.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 py-12 text-center text-slate-600" data-testid="categories-no-match">
              No categories match that search.
            </div>
          ) : (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {visible.map((c, i) => (
              <motion.div key={c.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <Link to={categoryUrl(c)} className="group relative block aspect-[4/3] overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-teal-900/40 to-cyan-900/30" data-testid={`public-category-${c.slug}`}>
                  {c.image_url ? (
                    <img src={resolveMediaUrl(c.image_url)} alt={c.name} loading="lazy" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-slate-900/20"><Compass className="h-10 w-10" /></div>
                  )}
                  {/* the title sits ON the photo: white type, no box, exactly like the home page tiles */}
                  <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
                  <h3 className="absolute inset-0 flex items-center justify-center px-3 text-center text-sm font-bold uppercase tracking-wide text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.85)] sm:text-base lg:text-lg">
                    {c.name}
                  </h3>
                </Link>
              </motion.div>
            ))}
          </div>
          )}
          </>
        )}
      </section>
      <Footer />
    </div>
  );
};

export default ActivitiesDirectoryPage;
